import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateProfile } from "../../redux/slices/authSlice";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  InputAdornment,
} from "@mui/material";
import ArrowBackIosOutlined from "@mui/icons-material/ArrowBackIosOutlined";
import PersonOutline from "@mui/icons-material/PersonOutline";
import WcOutlined from "@mui/icons-material/WcOutlined";
import PublicOutlined from "@mui/icons-material/PublicOutlined";
import LocationCityOutlined from "@mui/icons-material/LocationCityOutlined";

const UserProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, error } = useSelector((state) => state.auth);

  const handleBack = () => {
    const from = location.state?.from;
    // If 'from' is present and not the auth page itself, go to it
    if (from && from !== "/login" && from !== "/signup") {
      navigate(from);
    } else if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const [formData, setFormData] = useState({
    name: "",
    gender: "",
    country: "",
    city: "",
  });

  const [successMsg, setSuccessMsg] = useState("");
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        gender: user.gender || "",
        country: user.country || "",
        city: user.city || "",
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setLocalError("");

    const trimmedData = {
      ...formData,
      name: formData.name.trim(),
      country: formData.country ? formData.country.trim() : "",
      city: formData.city ? formData.city.trim() : "",
    };

    if (!trimmedData.name) {
      setLocalError("Name cannot be empty");
      return;
    }

    const result = await dispatch(updateProfile(trimmedData));
    if (!result.error) {
      setSuccessMsg("Profile updated successfully!");
      setTimeout(() => setSuccessMsg(""), 3000);
    }
  };

  if (!user) return null;

  const initials = user.name
    ? user.name.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase()
    : "U";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "radial-gradient(ellipse at 15% 10%, rgba(255, 155, 128, 0.12) 0%, transparent 45%), radial-gradient(ellipse at 85% 85%, rgba(255, 140, 56, 0.08) 0%, transparent 45%), #FAFAFA",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top Header with Logo */}
      <Box sx={{ py: 2.5, px: { xs: 2.5, sm: 4 } }}>
        <img
          src={require("../../assets/images/handpik.png")}
          alt="logo"
          width={162}
          height={32}
          style={{ cursor: "pointer", display: "block" }}
          onClick={() => navigate("/")}
        />
      </Box>

      <Container maxWidth="md" sx={{ mt: { xs: 1, sm: 2 }, mb: 8, flexGrow: 1 }}>
        {/* Back Button positioned cleanly above profile card */}
        <Box sx={{ display: "flex", mb: 2.5 }}>
          <IconButton
            onClick={handleBack}
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(6px)",
              border: "1px solid rgba(0, 0, 0, 0.06)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
              width: 42,
              height: 42,
              "&:hover": {
                backgroundColor: "#fff",
                transform: "scale(1.05)",
                boxShadow: "0 6px 16px rgba(0,0,0,0.09)",
              },
              transition: "all 0.2s ease",
            }}
          >
            <ArrowBackIosOutlined sx={{ fontSize: "20px", color: "#1a1a1a", ml: "5px" }} />
          </IconButton>
        </Box>

        <Grid container spacing={3.5} alignItems="flex-start">
          {/* Left Column: User Profile Summary Card */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3.5, sm: 4 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                borderRadius: "24px",
                background: "rgba(255, 255, 255, 0.96)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(0, 0, 0, 0.06)",
                boxShadow: "0 20px 50px -10px rgba(0, 0, 0, 0.05), 0 0 1px rgba(0,0,0,0.08)",
              }}
            >
              <Avatar
                sx={{
                  width: 108,
                  height: 108,
                  fontSize: "2.5rem",
                  mb: 2.5,
                  bgcolor: "#5abccb",
                  color: "#fff",
                  fontWeight: 700,
                  boxShadow: "0 10px 28px rgba(90, 188, 203, 0.35)",
                  border: "4px solid #FFFFFF",
                }}
              >
                {initials}
              </Avatar>

              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Lora', serif",
                  fontSize: "21px",
                  fontWeight: 600,
                  color: "#1A1A1A",
                  mb: 0.5,
                }}
              >
                {user.name}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#71717A",
                  fontSize: "13.5px",
                  mb: 2,
                  wordBreak: "break-all",
                }}
              >
                {user.email}
              </Typography>

              <Chip
                label={`Signed in via ${user.authProvider === "google" ? "Google" : "Email"}`}
                size="small"
                sx={{
                  borderRadius: "100px",
                  fontWeight: 500,
                  fontSize: "12px",
                  bgcolor: user.authProvider === "google" ? "rgba(66, 133, 244, 0.1)" : "rgba(255, 140, 56, 0.1)",
                  color: user.authProvider === "google" ? "#1a73e8" : "#FF8C38",
                  border: user.authProvider === "google" ? "1px solid rgba(66, 133, 244, 0.2)" : "1px solid rgba(255, 140, 56, 0.2)",
                  px: 0.5,
                }}
              />
            </Paper>
          </Grid>

          {/* Right Column: Edit Profile Form */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4.5 },
                borderRadius: "24px",
                background: "rgba(255, 255, 255, 0.96)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(0, 0, 0, 0.06)",
                boxShadow: "0 20px 50px -10px rgba(0, 0, 0, 0.05), 0 0 1px rgba(0,0,0,0.08)",
              }}
            >
              {/* Card Header */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    bgcolor: "rgba(255, 140, 56, 0.1)",
                    color: "#FF8C38",
                    borderRadius: "100px",
                    px: 1.8,
                    py: 0.4,
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    mb: 1.2,
                  }}
                >
                  Account Settings
                </Box>

                <Typography
                  sx={{
                    fontFamily: "'Lora', serif",
                    fontSize: { xs: "22px", sm: "26px" },
                    fontWeight: 600,
                    color: "#1A1A1A",
                    letterSpacing: "-0.01em",
                    mb: 0.5,
                  }}
                >
                  Edit Profile
                </Typography>
                <Typography variant="body2" sx={{ color: "#71717A", fontSize: "13.5px" }}>
                  Update your personal information and shopping preferences
                </Typography>
              </Box>

              <Divider sx={{ mb: 3, borderColor: "rgba(0, 0, 0, 0.06)" }} />

              {error && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: "12px", fontSize: "13px" }}>
                  {error}
                </Alert>
              )}
              {localError && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: "12px", fontSize: "13px" }}>
                  {localError}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutline sx={{ color: "#A1A1AA", fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "14px",
                          backgroundColor: "#FAF9F8",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "#FFFFFF",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "#FFFFFF",
                            "& fieldset": {
                              borderColor: "#FF8C38",
                              borderWidth: "1.5px",
                            },
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#FF8C38",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "14px",
                          backgroundColor: "#FAF9F8",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "#FFFFFF",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "#FFFFFF",
                            "& fieldset": {
                              borderColor: "#FF8C38",
                              borderWidth: "1.5px",
                            },
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#FF8C38",
                        },
                      }}
                    >
                      <InputLabel id="gender-label">Gender (Optional)</InputLabel>
                      <Select
                        labelId="gender-label"
                        id="gender"
                        name="gender"
                        value={formData.gender}
                        label="Gender (Optional)"
                        onChange={handleChange}
                        startAdornment={
                          <InputAdornment position="start">
                            <WcOutlined sx={{ color: "#A1A1AA", fontSize: 20, mr: 0.5 }} />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        <MenuItem value="Men">Men</MenuItem>
                        <MenuItem value="Women">Women</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Country (Optional)"
                      placeholder="Country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PublicOutlined sx={{ color: "#A1A1AA", fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "14px",
                          backgroundColor: "#FAF9F8",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "#FFFFFF",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "#FFFFFF",
                            "& fieldset": {
                              borderColor: "#FF8C38",
                              borderWidth: "1.5px",
                            },
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#FF8C38",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City (Optional)"
                      placeholder="City"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationCityOutlined sx={{ color: "#A1A1AA", fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "14px",
                          backgroundColor: "#FAF9F8",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "#FFFFFF",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "#FFFFFF",
                            "& fieldset": {
                              borderColor: "#FF8C38",
                              borderWidth: "1.5px",
                            },
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#FF8C38",
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3.5, display: "flex", justifyContent: { xs: "stretch", sm: "flex-start" } }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disableElevation
                    fullWidth={{ xs: true, sm: false }}
                    sx={{
                      py: 1.3,
                      px: { xs: 3, sm: 4.5 },
                      background: "linear-gradient(135deg, #FF9B80 0%, #FF8C38 100%)",
                      color: "#fff",
                      fontWeight: 600,
                      borderRadius: "14px",
                      textTransform: "none",
                      fontSize: "0.95rem",
                      letterSpacing: "0.2px",
                      boxShadow: "0 8px 20px rgba(255, 140, 56, 0.28)",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        background: "linear-gradient(135deg, #FF8C38 0%, #F27D26 100%)",
                        boxShadow: "0 10px 24px rgba(255, 140, 56, 0.36)",
                        transform: "translateY(-1px)",
                      },
                    }}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Floating toast for profile updates — eliminates layout shifts */}
      <Snackbar
        open={Boolean(successMsg)}
        autoHideDuration={3000}
        onClose={() => setSuccessMsg("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessMsg("")}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "14px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserProfile;
